import * as React from "react";
import {
  Box, Button, Dialog, DialogContent, DialogTitle, IconButton, Link,
  Stack, Typography,
} from "@mui/material";
import Close from "@mui/icons-material/Close";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import noImageFound from "../data/noimagefound.jpg";

type ProductDetails = {
  Product: string;
  Description: string;
  Type: string;
  Mrp: number;
  Images: string[];
  MoreDetails?: string | null;
  Link?: string | null;
};

export const ModelComponent = ({ open, handleClose, data }: {
  open: boolean;
  handleClose: () => void;
  data: ProductDetails;
}) => {
  const titleId = React.useId();
  const [imageIndex, setImageIndex] = React.useState(0);
  React.useEffect(() => { setImageIndex(0); }, [data, open]);
  const images = data.Images.length ? data.Images : [noImageFound];
  const currentIndex = imageIndex % images.length;
  const changeImage = (offset: number) => {
    setImageIndex((index) => (index + offset + images.length) % images.length);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      scroll="paper"
      fullWidth
      maxWidth="md"
      aria-labelledby={titleId}
      sx={{
        "& .MuiDialog-paper": {
          m: { xs: 1, sm: 3 },
          width: { xs: "calc(100% - 16px)", sm: "calc(100% - 48px)" },
          maxHeight: "calc(100% - 16px)",
          "@supports (height: 100dvh)": {
            maxHeight: { xs: "calc(100dvh - 16px)", sm: "calc(100dvh - 48px)" },
          },
          borderRadius: 2,
          minWidth: 0,
        },
      }}
    >
      <DialogTitle id={titleId} sx={{ pr: 8, p: 2, paddingRight: 8, overflowWrap: "anywhere" }}>
        {data.Product}
        <IconButton aria-label="Close product details" onClick={handleClose}
          sx={{ position: "absolute", right: 8, top: 8, width: 44, height: 44 }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 }, minWidth: 0 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={{ xs: 2, md: 3 }} sx={{ minWidth: 0 }}>
          <Box role="region" aria-label="Product images"
            sx={{ width: { xs: "100%", md: "45%" }, flexShrink: 0, minWidth: 0 }}>
            <Box sx={{ position: "relative", bgcolor: "grey.100", borderRadius: 1, overflow: "hidden" }}>
              <Box component="img" src={images[currentIndex]}
                alt={`${data.Product}, image ${currentIndex + 1} of ${images.length}`}
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  event.currentTarget.onerror = null;
                  if (!event.currentTarget.src.endsWith(noImageFound)) event.currentTarget.src = noImageFound;
                }}
                sx={{ display: "block", width: "100%", height: { xs: "min(50svh, 400px)", sm: 440 }, objectFit: "contain" }}
              />
              {images.length > 1 && <>
                <IconButton aria-label="Previous product image" onClick={() => changeImage(-1)}
                  sx={{ position: "absolute", left: 4, top: "calc(50% - 22px)", bgcolor: "background.paper", width: 44, height: 44 }}>
                  <ChevronLeft />
                </IconButton>
                <IconButton aria-label="Next product image" onClick={() => changeImage(1)}
                  sx={{ position: "absolute", right: 4, top: "calc(50% - 22px)", bgcolor: "background.paper", width: 44, height: 44 }}>
                  <ChevronRight />
                </IconButton>
              </>}
            </Box>
            {images.length > 1 && <Stack direction="row" justifyContent="center" flexWrap="wrap">
              {images.map((_, index) => (
                <IconButton key={index} aria-label={`Show product image ${index + 1}`}
                  aria-pressed={index === currentIndex} onClick={() => setImageIndex(index)}
                  sx={{ width: 44, height: 44 }}>
                  <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: index === currentIndex ? "primary.main" : "grey.400" }} />
                </IconButton>
              ))}
            </Stack>}
          </Box>
          <Stack spacing={2} sx={{ flex: 1, minWidth: 0, overflowWrap: "anywhere" }}>
            <Typography component="h3" variant="subtitle1" fontWeight={600}>Description</Typography>
            <Typography>{data.Description}{data.MoreDetails && <> <Link href={data.MoreDetails}>To Know More</Link></>}</Typography>
            <Box component="dl" sx={{ m: 0 }}>
              {[["Type", data.Type], ["MRP", `₹${data.Mrp}`]].map(([label, value]) => (
                <Stack key={label} direction="row" justifyContent="space-between" spacing={2} sx={{ py: 1 }}>
                  <Typography component="dt" color="text.secondary">{label}</Typography>
                  <Typography component="dd" sx={{ m: 0, minWidth: 0, textAlign: "right" }}>{value}</Typography>
                </Stack>
              ))}
            </Box>
            {data.Link && <Button variant="contained" href={data.Link} sx={{ alignSelf: { xs: "stretch", sm: "flex-start" }, minHeight: 44 }}>Buy On Amazon</Button>}
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
export default ModelComponent;
